import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { firestore } from '@/firebase'
import { useAuthStore } from './auth'
import createLogger from '@/utils/logger'

const logger = createLogger('stores/utm.ts')

interface UTMData {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  utm_id?: string
}

export const useUTMStore = defineStore('utm', () => {
  const authStore = useAuthStore()

  // State
  const utmData = ref<UTMData>({})
  const isCapturing = ref(false)
  const hasCaptured = ref(false)
  const lastCapturedAt = ref<Date | null>(null)

  // Computed
  const hasUTMData = computed(() => Object.keys(utmData.value).length > 0)
  const isAnonymous = computed(() => !authStore.user?.uid)

  const extractUTMParams = (): UTMData => {
    const urlParams = new URLSearchParams(window.location.search)
    const utmData: UTMData = {}

    // Standard UTM parameters
    const utmParams = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'utm_id',
    ]

    utmParams.forEach((param) => {
      const value = urlParams.get(param)
      if (value) {
        utmData[param as keyof UTMData] = value
      }
    })

    return utmData
  }

  const getUTMDocumentId = (): string => {
    const fromSessionStorage = sessionStorage.getItem('utm_document_id')
    if (fromSessionStorage) {
      return fromSessionStorage
    }
    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    sessionStorage.setItem('utm_document_id', newId)
    return newId
  }

  // Check if UTM has already been captured in this session
  const hasCapturedUTM = () => {
    return sessionStorage.getItem('utm_captured') === 'true'
  }

  // Mark UTM as captured for this session
  const markUTMCaptured = () => {
    sessionStorage.setItem('utm_captured', 'true')
    hasCaptured.value = true
    lastCapturedAt.value = new Date()
  }

  /**
   * Store UTM data in sessionStorage
   */
  const storeUTMData = (utmData: UTMData): void => {
    if (Object.keys(utmData).length > 0) {
      sessionStorage.setItem('utm_data', JSON.stringify(utmData))
      logger.log('UTM data stored', { fieldCount: Object.keys(utmData).length })
    }
  }

  /**
   * Retrieve UTM data from sessionStorage
   */
  const getStoredUTMData = (): UTMData | null => {
    try {
      const stored = sessionStorage.getItem('utm_data')
      return stored ? JSON.parse(stored) : null
    } catch (error) {
      console.error('Error retrieving UTM data:', error)
      return null
    }
  }

  /**
   * Clear stored UTM data
   */
  const clearStoredUTMData = (): void => {
    sessionStorage.removeItem('utm_data')
  }

  /**
   * Save UTM data to Firestore
   */
  const saveUTMToFirestore = async (utmData: UTMData) => {
    if (Object.keys(utmData).length === 0) {
      logger.log('No UTM data to save')
      return
    }

    try {
      // Generate a unique ID for this session if no user ID
      const sessionId = getUTMDocumentId()
      const utmDocRef = doc(firestore, 'utm_captures', sessionId)

      // Check if document already exists
      const existingDoc = await getDoc(utmDocRef)

      if (existingDoc.exists()) {
        logger.log('UTM data already exists for this session, updating user info...')
        // Update existing document with user information if available
        await updateUTMDataInFirestore(utmData, sessionId)
        return
      }

      // Save UTM data with timestamp
      await setDoc(utmDocRef, {
        ...utmData,
        capturedAt: new Date(),
        userId: authStore.user?.uid || null,
        userEmail: authStore.user?.email || null,
        sessionId: sessionId,
        pageUrl: window.location.href,
        referrer: document.referrer || null,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      })

      logger.log('UTM data saved to Firestore', { sessionId })
    } catch (error) {
      console.error('Error saving UTM data to Firestore:', error)
    }
  }

  /**
   * Update existing UTM document with user information
   */
  const updateUTMDataInFirestore = async (utmData: UTMData, sessionId: string) => {
    if (Object.keys(utmData).length === 0) {
      return
    }

    try {
      const utmDocRef = doc(firestore, 'utm_captures', sessionId)
      await updateDoc(utmDocRef, {
        userId: authStore.user?.uid || null,
        userEmail: authStore.user?.email || null,
      })
      logger.log('UTM document updated with user information')
    } catch (error) {
      console.error('Error updating UTM data in Firestore:', error)
    }
  }

  /**
   * Capture UTM parameters from URL and store them
   */
  const captureUTMParams = async (options: { update: boolean } = { update: false }) => {
    if (isCapturing.value || (hasCapturedUTM() && !options.update)) {
      logger.log('UTM already captured or capture in progress')
      return
    }

    isCapturing.value = true

    try {
      // Extract UTM parameters from URL
      const extractedUTM = getUTMData()
      utmData.value = extractedUTM

      if (Object.keys(extractedUTM).length > 0) {
        // Save to Firestore
        await saveUTMToFirestore(extractedUTM)
        logger.log('UTM parameters captured and saved', {
          fieldCount: Object.keys(extractedUTM).length,
        })
      } else {
        logger.log('No UTM parameters found in URL')
      }

      // Mark as captured for this session
      markUTMCaptured()
    } catch (error) {
      console.error('Error capturing UTM parameters:', error)
    } finally {
      isCapturing.value = false
    }
  }

  /**
   * Get UTM data with fallback to stored data
   */
  const getUTMData = () => {
    const urlUTM = extractUTMParams()

    // If we have URL parameters, use them and store them
    if (Object.keys(urlUTM).length > 0) {
      storeUTMData(urlUTM)
      utmData.value = urlUTM
      return urlUTM
    }

    // Otherwise, return stored data if available
    const storedUTM = getStoredUTMData()
    if (storedUTM) {
      utmData.value = storedUTM
    }
    return storedUTM || {}
  }

  /**
   * Clear UTM data
   */
  const clearUTMData = () => {
    utmData.value = {}
    clearStoredUTMData()
    sessionStorage.removeItem('utm_captured')
    hasCaptured.value = false
    lastCapturedAt.value = null
  }

  /**
   * Initialize UTM capture
   */
  const initUTMCapture = async () => {
    logger.log('Initializing UTM capture...')
    await captureUTMParams()
  }

  /**
   * Get UTM data for analytics
   */
  const getUTMAnalytics = () => {
    return {
      utmData: utmData.value,
      hasCaptured: hasCaptured.value,
      lastCapturedAt: lastCapturedAt.value,
      isAnonymous: isAnonymous.value,
      userId: authStore.user?.uid,
      userEmail: authStore.user?.email,
    }
  }

  /**
   * Set custom UTM data and save to Firestore
   */
  const setCustomUTMData = async (customUTMData: UTMData) => {
    if (Object.keys(customUTMData).length === 0) {
      logger.log('No custom UTM data to save')
      return
    }

    try {
      // Set the UTM data in the store
      utmData.value = customUTMData

      // Store in sessionStorage
      storeUTMData(customUTMData)

      // Save to Firestore using the existing method
      await saveUTMToFirestore(customUTMData)

      // Mark as captured
      markUTMCaptured()

      logger.log('Custom UTM data set and saved', {
        fieldCount: Object.keys(customUTMData).length,
      })
    } catch (error) {
      console.error('Error setting custom UTM data:', error)
    }
  }

  return {
    // State
    utmData,
    isCapturing,
    hasCaptured,
    lastCapturedAt,

    // Computed
    hasUTMData,
    isAnonymous,

    // Actions
    captureUTMParams,
    getUTMData,
    clearUTMData,
    initUTMCapture,
    getUTMAnalytics,
    hasCapturedUTM,
    markUTMCaptured,
    updateUTMDataInFirestore,
    setCustomUTMData,
  }
})
